<script setup>
import { useAuthStore } from "@/stores";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { BellIcon, PlusIcon, XIcon } from "@heroicons/vue/outline";
import { MenuAlt2Icon } from "@heroicons/vue/solid";
import { computed, onBeforeMount } from "vue";
import { useRoute, useRouter } from "vue-router";
import CreateUnitDialog from "@/components/unit/CreateUnitDialog.vue";
import { ref } from "vue";
import { http } from "@/composable/httpService";
import TopNotification from "@/components/layout/header/TopNotification.vue";
import TopAppsMenu from "@/components/layout/header/TopAppsMenu.vue";
import AppProfileMenu from "./AppProfileMenu.vue";
import AppAdminPortalMenu from "./AppAdminPortalMenu.vue";
import GlobalSearchDialog from "@/components/layout/GlobalSearchDialog.vue";
import AppSmallScreenMenu from "./AppSmallScreenMenu.vue";
import AppReportMenu from "./AppReportMenu.vue";
import AppResourceMenu from "./AppResourceMenu.vue";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const isSuperAdmin = computed(() => authStore.isSuperAdmin);
const isAccountManager = computed(() => authStore.isAccountManager);
const isDealerManager = computed(() => authStore.isDealerManager);
const isMultiStoreManager = computed(() => authStore.isMultiStoreManager);

const isCreateUnitDialogOpen = ref(false);
const loadingApps = ref(false);
const apps = ref([]);

const selectedRouteName = computed(() => route.name);
const selectedRouteFullPath = computed(() => route.fullPath);

const navigation = ref({
  main: [
    { name: "Home", href: "/", current: true, route: "home" },
    {
      name: "Reports",
      href: "/reports",
      current: false,
      route: "reports",
    },
    {
      name: "My Comments",
      href: "/my-comments",
      current: false,
      route: "mynotes",
    },
    {
      name: "Training Resources",
      href: "/resource-manager",
      current: false,
      route: "filemanager",
    },
    {
      name: "Active Role Plays",
      href: "/active-role-plays",
      current: false,
      route: "active-role-plays",
    },
  ],
});

const filteredNavigation = computed(() => {
  let menus = [...navigation.value.main];

  if (
    isSuperAdmin.value ||
    isAccountManager.value ||
    isDealerManager.value ||
    isMultiStoreManager.value
  ) {
    menus = [
      ...menus,
      {
        name: "Admin Portal",
        href: "/central/manage-users",
        current: false,
        route: "#",
      },
    ];
  }

  return menus;
});

const handleGoToRoute = (routePath, fullPath = false) => {
  if (fullPath) {
    router.push(routePath);
    return;
  }
  router.push({ name: routePath });
};

const setCreateUnitDialog = (isOpen) => {
  isCreateUnitDialogOpen.value = isOpen;
};

const handleFetchCentralApps = () => {
  loadingApps.value = true;
  http()
    .get("/central/get-app-menu")
    .then(({ data }) => {
      apps.value = data;
    })
    .finally(() => (loadingApps.value = false));
};

onBeforeMount(() => {
  handleFetchCentralApps();
});
</script>
<template>
  <Disclosure as="nav" class="bg-brand sticky top-0 z-50" v-slot="{ open }">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex h-16 justify-between">
        <div class="flex">
          <div class="flex shrink-0 items-center">
            <img
              class="block w-8 md:h-auto lg:hidden"
              src="@/assets/ncm-logo.svg"
              alt="Your Company"
            />
            <img
              class="hidden w-20 md:h-auto lg:block"
              src="@/assets/ncm-logo.svg"
              alt="Your Company"
            />
          </div>
          <div class="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-6">
            <template v-for="(item, index) in filteredNavigation" :key="index">
              <RouterLink
                :to="item.href"
                v-if="
                  item.name === 'Home' ||
                  item.name === 'Active Role Plays' ||
                  item.name === 'My Comments' ||
                  item.name === 'News Feed' ||
                  item.name === 'Training Resources'
                "
                :class="[
                  selectedRouteName === item.route
                    ? 'border-white text-white  '
                    : 'border-transparent text-untitled-gray-400 hover:border-white hover:text-white',
                  'inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium',
                  {
                    'hidden lg:flex':
                      item.name === 'Training Resources' ||
                      item.name === 'My Comments',
                    'hidden xl:flex': item.name === 'Active Role Plays',
                  },
                ]"
                :aria-current="item.current ? 'page' : undefined"
                >{{ item.name }}</RouterLink
              >

              <AppReportMenu
                v-else-if="item.name === 'Reports'"
                class="hidden md:block"
              />
              <AppAdminPortalMenu
                class="hidden 2xl:block"
                v-has-restrict-learner
                v-else-if="item.name === 'Admin Portal'"
              />
              <AppResourceMenu
                class="hidden xl:block"
                v-else-if="item.name === 'Resource Manager'"
              />
            </template>

            <AppSmallScreenMenu class="2xl:hidden" />
            <!--  -->
          </div>
        </div>
        <div class="hidden sm:ml-6 sm:flex sm:items-center gap-2">
          <button
            v-has-restrict-learner
            class="bg-brand-light flex gap-2 px-4 text-sm rounded text-white py-2 justify-center items-center"
            size="lg"
            @click="setCreateUnitDialog(true)"
          >
            <PlusIcon class="size-5" aria-hidden="true" />
            Upload Content
          </button>
          <div class="ml-2 flex-shrink-0 flex justify-center items-center">
            <GlobalSearchDialog class="text-white" />
          </div>
          <button
            type="button"
            v-if="false"
            class="relative p-1 text-kintz-primary-4 hover:text-kintz-primary-2 focus:ring-2 focus:ring-kintz-primary-2 focus:ring-offset-2 focus:outline-hidden"
          >
            <span class="absolute -inset-1.5" />
            <span class="sr-only">View notifications</span>
            <BellIcon class="size-6" aria-hidden="true" />
          </button>

          <TopNotification :white="true" />
          <TopAppsMenu
            :white="true"
            v-if="!loadingApps"
            :apps="apps"
            kintz-app
          />

          <!-- Profile dropdown -->
          <AppProfileMenu />
        </div>
        <div class="-mr-2 flex items-center gap-2 sm:hidden">
          <TopNotification />
          <TopAppsMenu v-if="!loadingApps" :apps="apps" kintz-app />
          <AppProfileMenu />
          <!-- Mobile menu button -->
          <DisclosureButton
            class="relative inline-flex items-center justify-center rounded-md bg-kintz-primary-1 p-2 text-kintz-primary-2 hover:bg-kintz-primary-3 focus:ring-2 focus:ring-kintz-primary-2 focus:ring-offset-2 focus:outline-hidden"
          >
            <span class="absolute -inset-0.5" />
            <span class="sr-only">Open main menu</span>
            <MenuAlt2Icon
              v-if="!open"
              class="block size-6 text-white"
              aria-hidden="true"
            />
            <XIcon v-else class="block size-6 text-white" aria-hidden="true" />
          </DisclosureButton>
        </div>
      </div>
    </div>

    <DisclosurePanel class="sm:hidden">
      <div class="space-y-1 pt-2 pb-3">
        <template v-for="(item, index) in filteredNavigation" :key="index">
          <DisclosureButton
            v-if="
              item.name === 'Home' ||
              item.name === 'Active Role Plays' ||
              item.name === 'My Comments' ||
              item.name === 'News Feed' ||
              item.name === 'Training Resources'
            "
            as="button"
            @click="handleGoToRoute(item.route)"
            :class="[
              selectedRouteName === item.route
                ? 'border-white text-white'
                : 'border-transparent text-untitled-gray-400 hover:border-white hover:text-white',
              'block border-l-4 py-2 pr-4 pl-3 text-base font-medium',
            ]"
            :aria-current="item.current ? 'page' : undefined"
          >
            {{ item.name }}
          </DisclosureButton>

          <AppAdminPortalMenu
            class="mr-3"
            v-has-restrict-learner
            v-else-if="item.name === 'Admin Portal'"
          >
            <template #default="{ filteredRoutes }">
              <div
                class="flex justify-start px-4 items-center text-brand-light uppercase font-medium text-sm"
              >
                <h1>Admin Portal</h1>
              </div>
              <DisclosureButton
                v-for="(item, index) in filteredRoutes"
                :key="index"
                as="button"
                @click="
                  item.urlType === 'lms'
                    ? handleGoToRoute(item.to, true)
                    : window.open(item.to, '_blank')
                "
                :class="[
                  selectedRouteFullPath === item.to
                    ? 'border-white text-white'
                    : 'border-transparent text-untitled-gray-400 hover:border-white hover:text-white',
                  'block border-l-4 py-2 pr-4 pl-3 text-base font-medium',
                ]"
                :aria-current="item.current ? 'page' : undefined"
              >
                {{ item.name }}
              </DisclosureButton>
            </template>
          </AppAdminPortalMenu>
        </template>
      </div>
    </DisclosurePanel>
    <CreateUnitDialog
      :isCreateUnitDialogOpen="isCreateUnitDialogOpen"
      @closeCreateUnitDialog="setCreateUnitDialog(false)"
    />
  </Disclosure>
</template>

<style lang="scss" scoped></style>
